import CloseIcon from '@mui/icons-material/Close'
import DownloadIcon from '@mui/icons-material/Download'
import PrintIcon from '@mui/icons-material/Print'
import { Dialog, DialogContent, DialogTitle, IconButton, Stack } from '@mui/material'
import { useFileDownloader } from 'hooks/useFileDownloader'

export type PdfViewerModalProps = {
	open: boolean
	onClose: () => void
	pdfUrl: string
	title: string
	// Name the download is saved under; defaults to the title as a PDF
	fileName?: string
}

// Suppresses the browser's native PDF-viewer chrome (toolbar + thumbnail
// panel) via Acrobat-style open parameters, so only our own DialogTitle bar
// shows — otherwise Chrome/Firefox's built-in PDF viewer renders a second,
// redundant toolbar inside the iframe. This also hides the native
// download/print buttons, so we provide our own in the title bar instead.
const withViewerParams = (pdfUrl: string): string => `${pdfUrl}#toolbar=0&navpanes=0`

// These PDFs are hosted on HubSpot's CDN (a different origin than this app),
// so `fetch` only succeeds if that host sends permissive CORS headers. When
// it doesn't (or the request otherwise fails), fall back to opening the raw
// URL in a new tab so the guide is still reachable — just without a forced
// download/print.
const fetchPdfBlob = async (pdfUrl: string): Promise<Blob | null> => {
	try {
		const response = await fetch(pdfUrl)
		if (!response.ok) return null
		return await response.blob()
	} catch {
		return null
	}
}

// `window.open` only escapes the popup blocker while the click's user gesture
// is still active, and awaiting the fetch above ends it — so both handlers
// reserve a blank tab synchronously and only decide what to put in it once the
// fetch settles. A reserved tab can't be opened with `noopener` (that makes
// `window.open` return null and leaves us no handle), so the fallback
// navigation drops `opener` itself before pointing the tab at the raw PDF.
const reserveTab = (): Window | null => window.open('', '_blank')

const showRawPdf = (reservedTab: Window | null, pdfUrl: string): void => {
	if (!reservedTab) {
		// Blocked even during the gesture — a direct open is all we could have
		// done anyway, and some blockers only reject the blank pre-navigation tab.
		window.open(pdfUrl, '_blank', 'noopener,noreferrer')
		return
	}
	reservedTab.opener = null
	reservedTab.location.replace(pdfUrl)
}

export const PdfViewerModal: React.FC<PdfViewerModalProps> = ({
	open,
	onClose,
	pdfUrl,
	title,
	fileName = `${title}.pdf`
}) => {
	const download = useFileDownloader()

	const handleDownload = async () => {
		const reservedTab = reserveTab()
		const blob = await fetchPdfBlob(pdfUrl)
		if (!blob) {
			showRawPdf(reservedTab, pdfUrl)
			return
		}
		// The download happens in this tab, so the reserved one is dead weight.
		reservedTab?.close()
		download(blob, fileName)
	}

	const handlePrint = async () => {
		const reservedTab = reserveTab()
		const blob = await fetchPdfBlob(pdfUrl)
		if (!blob) {
			showRawPdf(reservedTab, pdfUrl)
			return
		}
		// A blob: URL shares this page's origin, so (unlike the raw cross-origin
		// pdfUrl) we're allowed to call .print() on the window we open it in.
		// Navigating the reserved tab would swap its document out from under the
		// `load` listener, so the blob path opens its own window and discards the
		// reserved tab.
		reservedTab?.close()
		const blobUrl = window.URL.createObjectURL(blob)
		const printWindow = window.open(blobUrl, '_blank')
		if (!printWindow) {
			// Popup blocked — nothing will consume the URL, so release it now.
			window.URL.revokeObjectURL(blobUrl)
			return
		}
		printWindow.addEventListener('load', () => {
			printWindow.print()
			// The document is already loaded at this point, so the URL is no
			// longer needed; revoking keeps the blob from leaking for the life
			// of the tab.
			window.URL.revokeObjectURL(blobUrl)
		})
	}

	return (
		<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
			{/* The app-wide MuiDialogTitle override is sized for form dialogs (32px
			    top padding, 24px type), which reads as a 90px-tall bar above a
			    document viewer. Overridden here to a compact toolbar row: title and
			    actions share one line, and `background.default` tints the bar so it
			    separates from the white PDF page in light mode (in dark mode
			    `background.default` equals `background.paper`, so the bar keeps the
			    flat navy look the rest of the dialog already has). */}
			<DialogTitle
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					gap: 2,
					py: 1.5,
					px: 3,
					fontSize: 18,
					bgcolor: 'background.default',
					borderBottom: 1,
					borderColor: 'divider'
				}}
			>
				{title}
				<Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
					<IconButton
						size="small"
						aria-label="print"
						onClick={handlePrint}
						sx={{ color: 'text.secondary' }}
					>
						<PrintIcon fontSize="small" />
					</IconButton>
					<IconButton
						size="small"
						aria-label="download"
						onClick={handleDownload}
						sx={{ color: 'text.secondary' }}
					>
						<DownloadIcon fontSize="small" />
					</IconButton>
					<IconButton
						size="small"
						aria-label="close"
						onClick={onClose}
						sx={{ color: 'text.secondary' }}
					>
						<CloseIcon fontSize="small" />
					</IconButton>
				</Stack>
			</DialogTitle>
			<DialogContent
				sx={{ height: '85vh', p: 0, overflow: 'hidden', bgcolor: 'background.default' }}
			>
				<iframe
					src={withViewerParams(pdfUrl)}
					title={title}
					width="100%"
					height="100%"
					style={{ border: 'none', display: 'block' }}
				/>
			</DialogContent>
		</Dialog>
	)
}
